import PropTypes from 'prop-types'
import React, {useState, useEffect, useCallback, useRef} from 'react'
import {FormattedMessage} from 'react-intl'
import {
    Accordion,
    AccordionButton,
    AccordionItem,
    AccordionPanel,
    Box,
    Flex,
    Skeleton,
    Text,
    useMultiStyleConfig
} from '@chakra-ui/react'
import {ArrowIcon} from '../../../components/icons'
import useOcapiSite from '../../../hooks/useOcapiSite'
import lazyLoadable from '../../../utils/lazy-loadable'
import {RECOMMENDED_SPARE_PARTS_TYPE} from '../../../constants'
import useEffectEvent from '../../../hooks/use-effect-event'
import textStyles from '../../../theme/foundations/textStyles'

const fallback = <Skeleton height="25vh" width="100%" />
const HowToUse = lazyLoadable(() => import('./how-to-use'), {
    fallback,
    ssr: false
})
const SpareParts = lazyLoadable(() => import('./spare-parts'), {
    fallback,
    ssr: false
})

const AfterSales = ({product, onRecommendedProductClick, ...props}) => {
    const styles = useMultiStyleConfig('AfterSales')
    const {brightcoveEnabled} = useOcapiSite()
    const howToUseRef = useRef(null)
    const hasSpareParts = product?.recommendations?.some(
        ({recommendationType}) => recommendationType.value === RECOMMENDED_SPARE_PARTS_TYPE
    )
    const hasFaqs =
        (product?.c_assemblyVideos?.[0] && brightcoveEnabled) ||
        product?.c_maintenance ||
        product?.c_documents

    /*
        This state is used to keep track of the opened item index
        and of height of the first accordion item when it is opened
        and closed.
    */
    const [itemsObj, setItemsObj] = useState({
        openedIndex: -1,
        firstItemOpenedHeight: 0,
        firstItemClosedHeight: 0,
        needsToScroll: false
    })

    const onNeedToScroll = useEffectEvent(() =>
        window.scrollBy({
            top: -itemsObj.firstItemOpenedHeight + itemsObj.firstItemClosedHeight,
            left: 0
        })
    )
    useEffect(() => {
        if (itemsObj.needsToScroll) {
            onNeedToScroll(itemsObj.openedIndex)
        }
    }, [itemsObj.needsToScroll, itemsObj.openedIndex, onNeedToScroll])

    const handleOnChange = useCallback((openedIndex) => {
        setItemsObj((itemsObj) => ({
            openedIndex: openedIndex,

            firstItemOpenedHeight:
                itemsObj.openedIndex === 0
                    ? howToUseRef.current?.offsetHeight
                    : itemsObj.firstItemOpenedHeight,

            firstItemClosedHeight:
                itemsObj.openedIndex !== 0
                    ? howToUseRef.current?.offsetHeight
                    : itemsObj.firstItemClosedHeight,

            needsToScroll: itemsObj.openedIndex === 0 && openedIndex === 1
        }))
    }, [])

    if (!hasSpareParts && !hasFaqs) {
        return null
    }

    return (
        <Box {...styles.container}>
            <Accordion
                {...props}
                onChange={handleOnChange}
                allowMultiple
                index={itemsObj.openedIndex}
                allowToggle
            >
                {hasFaqs && (
                    <AccordionItem
                        {...styles.accordionItem}
                        ref={(el) => {
                            howToUseRef.current = el
                        }}
                    >
                        {({isExpanded}) => {
                            return (
                                <>
                                    <Text as="h3">
                                        <AccordionButton
                                            {...styles.accordionButton}
                                            data-testid="button_documents"
                                            {...styles.accordionButtonLabel}
                                            {...textStyles['Regular XLarge']}
                                        >
                                            <FormattedMessage
                                                defaultMessage="How to use / Documents"
                                                id="product_detail.after_sales.documents"
                                            />
                                            <ArrowIcon
                                                boxSize={4}
                                                transform={isExpanded && 'rotate(-180deg)'}
                                                transition=".2s"
                                            />
                                        </AccordionButton>
                                    </Text>
                                    <AccordionPanel>
                                        <HowToUse product={product} isExpanded={isExpanded} />
                                    </AccordionPanel>
                                </>
                            )
                        }}
                    </AccordionItem>
                )}
                {hasSpareParts && (
                    <AccordionItem {...styles.accordionItem}>
                        {({isExpanded}) => (
                            <>
                                <Text as="h3">
                                    <AccordionButton
                                        {...styles.accordionButton}
                                        data-testid="button_spare_parts"
                                        {...textStyles['Regular XLarge']}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Spare Parts"
                                            id="product_detail.sections.spareParts"
                                        />
                                        <ArrowIcon
                                            boxSize={4}
                                            transform={isExpanded && 'rotate(-180deg)'}
                                            transition=".2s"
                                        />
                                    </AccordionButton>
                                </Text>
                                {isExpanded && (
                                    <Flex {...styles.sparePartsAccordionPanel}>
                                        <AccordionPanel paddingInline={0}>
                                            <SpareParts
                                                product={product}
                                                onRecommendedProductClick={
                                                    onRecommendedProductClick
                                                }
                                            />
                                        </AccordionPanel>
                                    </Flex>
                                )}
                            </>
                        )}
                    </AccordionItem>
                )}
            </Accordion>
        </Box>
    )
}

AfterSales.propTypes = {
    product: PropTypes.object,
    onRecommendedProductClick: PropTypes.func
}

export default AfterSales
